import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFlags } from "@/hooks/useFlags";
import { islandsFor } from "@/lib/islands";
import { PickupSelect } from "@/components/PickupSelect";
import { DateField, fromISO } from "@/components/DateField";

export function SearchBar({ compact = false }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const islands = islandsFor(useFlags().v3_enabled);
  const today = new Date().toISOString().split("T")[0];
  const [f, setF] = useState({ country: "", pickup: "", start_date: today, end_date: "" });

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, val]) => { if (val) p.set(k, val); });
    nav(`/search?${p.toString()}`);
  };

  const minStart = fromISO(today);

  return (
    <form onSubmit={submit} data-testid="search-form"
      className={`bg-white rounded-2xl shadow-search p-3 sm:p-4 ${compact ? "" : "backdrop-blur-md"}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-muted2 flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> {t("search.country")}</label>
          <select data-testid="search-country-select" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, pickup: "" })}
            className="w-full h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-canard">
            <option value="">{t("search.allCountries")}</option>
            {islands.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-muted2 flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> {t("search.pickup")}</label>
          <PickupSelect island={f.country} value={f.pickup} onChange={(val) => setF({ ...f, pickup: val })} testid="search-pickup-input" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-muted2 flex items-center gap-1 mb-1"><CalendarIcon className="w-3 h-3" /> {t("search.startDate")}</label>
          <DateField value={f.start_date} testid="search-start-input" placeholder={t("search.startDate")}
            disabled={{ before: minStart }}
            onChange={(val) => setF({ ...f, start_date: val, end_date: f.end_date && val && f.end_date < val ? "" : f.end_date })} />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-muted2 flex items-center gap-1 mb-1"><CalendarIcon className="w-3 h-3" /> {t("search.endDate")}</label>
          <DateField value={f.end_date} testid="search-end-input" placeholder={t("search.endDate")}
            disabled={{ before: fromISO(f.start_date) || minStart }}
            onChange={(val) => setF({ ...f, end_date: val })} />
        </div>
      </div>
      <Button type="submit" data-testid="search-submit-button"
        className="w-full mt-3 h-12 rounded-xl bg-emeraude hover:bg-emeraude-dark text-white font-heading font-bold text-base shadow-cta">
        <Search className="w-5 h-5 mr-2" /> {t("hero.search")}
      </Button>
    </form>
  );
}
